import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import BucketApi from "api/bucketApi";
import { useAuth } from "auth-context/auth.context";
import FileRow from "./components/fileRow";
import FileDialog from "components/FileDialog";
import FileInput from "./components/fileInput";

const UserAttachments = ({ userIdentification }) => {
  const { user } = useAuth();
  const [signatureUrl, setSignatureUrl] = useState("");
  const [hasSignature, setHasSignature] = useState(false);
  const [professionalCardUrl, setProfessionalCardUrl] = useState("");
  const [hasProfessionalCard, setHasProfessionalCard] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState(null);
  const [dialogTitle, setDialogTitle] = useState("");

  const signatureInputRef = useRef(null);
  const professionalCardInputRef = useRef(null);

  const signatureFileName = "firma_digital.jpg";
  const professionalCardFileName = "tarjeta_profesional.pdf";
  const signatureFileType = "signature";
  const professionalCardFileType = "professional_card";

  const isOperator = user?.groups?.includes("OPERADOR(A)");

  useEffect(() => {
    checkSignature();
    if (isOperator) {
      checkProfessionalCard();
    }
  }, [userIdentification, isOperator]);

  const checkSignature = async () => {
    try {
      const response = await BucketApi.checkFileExists(signatureFileName, signatureFileType);
      setHasSignature(response.data.exists);
      if (response.data.exists) {
        fetchSignatureUrl();
      } else {
        setSignatureUrl("");
      }
    } catch (error) {
      console.error("Error checking signature existence:", error);
      setHasSignature(false);
      setSignatureUrl("");
    }
  };

  const checkProfessionalCard = async () => {
    try {
      const response = await BucketApi.checkFileExists(
        professionalCardFileName,
        professionalCardFileType
      );
      setHasProfessionalCard(response.data.exists);
      if (response.data.exists) {
        fetchProfessionalCardUrl();
      } else {
        setProfessionalCardUrl("");
      }
    } catch (error) {
      console.error("Error checking professional card existence:", error);
      setHasProfessionalCard(false);
      setProfessionalCardUrl("");
    }
  };

  const fetchSignatureUrl = async () => {
    try {
      const response = await BucketApi.getFileUrl(signatureFileName, signatureFileType);
      setSignatureUrl(response.data.file_url);
    } catch (error) {
      console.error("Error fetching signature URL:", error);
      setSignatureUrl("");
    }
  };

  const fetchProfessionalCardUrl = async () => {
    try {
      const response = await BucketApi.getFileUrl(
        professionalCardFileName,
        professionalCardFileType
      );
      setProfessionalCardUrl(response.data.file_url);
    } catch (error) {
      console.error("Error fetching professional card URL:", error);
      setProfessionalCardUrl("");
    }
  };

  const handleFileChange = async (event, fileType) => {
    const file = event.target.files[0];
    if (file) {
      try {
        if (fileType === signatureFileType) {
          await BucketApi.uploadFile(file, signatureFileName, signatureFileType);
          alert("Firma digital subida exitosamente.");
          checkSignature();
        } else if (fileType === professionalCardFileType) {
          await BucketApi.uploadFile(file, professionalCardFileName, professionalCardFileType);
          alert("Tarjeta profesional subida exitosamente.");
          checkProfessionalCard();
        }
      } catch (error) {
        console.error(`Error uploading ${fileType}:`, error);
        alert(
          `Error al subir ${
            fileType === signatureFileType ? "la firma digital" : "la tarjeta profesional"
          }. Por favor, intente de nuevo.`
        );
      }
    }
  };

  const handleViewFile = (fileType) => {
    if (fileType === signatureFileType) {
      setDialogTitle("Firma Digital");
      setDialogContent(<img src={signatureUrl} alt="Firma Digital" style={{ maxWidth: "100%" }} />);
    } else {
      setDialogTitle("Tarjeta Profesional");
      setDialogContent(
        <embed
          src={professionalCardUrl}
          type="application/pdf"
          width="100%"
          height="500px"
          style={{ border: "none" }}
          title="Tarjeta Profesional"
        />
      );
    }
    setOpenDialog(true);
  };

  const handleUpdateFile = (fileType) => {
    if (fileType === signatureFileType) {
      signatureInputRef.current.click();
    } else {
      professionalCardInputRef.current.click();
    }
  };

  return (
    <Card sx={{ height: "100%" }}>
      <SuiBox pt={2} px={2}>
        <SuiTypography variant="h6" fontWeight="medium" textTransform="capitalize">
          Archivos Adjuntos
        </SuiTypography>
      </SuiBox>
      <SuiBox p={2}>
        <Grid container spacing={2}>
          <FileRow
            title={hasSignature ? "Firma digital disponible" : "Sin firma digital"}
            hasFile={hasSignature}
            onView={() => handleViewFile(signatureFileType)}
            onUpdate={() => handleUpdateFile(signatureFileType)}
          />

          {isOperator && (
            <FileRow
              title={
                hasProfessionalCard ? "Tarjeta profesional disponible" : "Sin tarjeta profesional"
              }
              hasFile={hasProfessionalCard}
              onView={() => handleViewFile(professionalCardFileType)}
              onUpdate={() => handleUpdateFile(professionalCardFileType)}
            />
          )}
        </Grid>
      </SuiBox>

      <FileDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title={dialogTitle}
        content={dialogContent}
      />

      <FileInput
        ref={signatureInputRef}
        accept="image/*"
        onChange={(e) => handleFileChange(e, signatureFileType)}
      />

      <FileInput
        ref={professionalCardInputRef}
        accept="application/pdf"
        onChange={(e) => handleFileChange(e, professionalCardFileType)}
      />
    </Card>
  );
};

UserAttachments.propTypes = {
  userIdentification: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default UserAttachments;
